<template>
  <div class="settings-pane">
    <div
      v-if="header && $slots['subheader']"
      class="settings-pane__header"
    >
      <div class="settings-pane__header__inner">
        <div>
          <div class="settings-pane__header__title">{{ header }}</div>
          <div class="settings-pane__header__text">
            <slot name="subheader"></slot>
          </div>
        </div>
        <div v-if="$slots['header-action']">
          <slot name="header-action" />
        </div>
      </div>
    </div>
    <div class="settings-pane__scroll">
      <div class="settings-pane__container">
        <div
          v-if="$slots['body']"
          class="settings-pane__body"
        >
          <slot name="body" />
        </div>
      </div>
    </div>

    <div class="settings-pane__footer">
      <DarwinButton
        class="settings-pane__cancel"
        size="md"
        kind="secondary"
        flair="rounded"
        @click="close"
      >
        Close
      </DarwinButton>
      <slot name="footer" />
    </div>
    <slot />
  </div>
</template>

<script setup lang="ts">
import DarwinButton from '@/uiKit/Button/DarwinButton.vue'
import { useRouter } from 'vue-router'

withDefaults(defineProps<{ header?: string }>(), { header: '' })

const router = useRouter()

const close = (): void => {
  router.push({ query: { ...router.currentRoute.value.query, settings: undefined } })
}
</script>

<style lang="scss" scoped>
@mixin sectionAlignment {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.settings-pane {
  @include typographyRegularBody200;
  display: flex;
  flex-direction: column;

  height: 100%;
  overflow: hidden;
  background-color: var(--color-surface-default);

  &__header {
    position: relative;
    padding: 16px 32px;
    @include sectionAlignment;

    &:after {
      content: '';
      position: absolute;
      bottom: -10px;
      left: 0;
      right: 0;
      height: 10px;
      background: linear-gradient(to bottom, rgba(145, 169, 192, 0.1), transparent);
      z-index: 1;
    }

    &__inner {
      display: flex;
      flex-direction: row;
      flex-grow: 1;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      max-width: 768px;
    }

    &__title {
      @include typographyBoldBody600;
      color: var(--color-text-default);
      margin-bottom: 4px;
    }

    &__text {
      @include typographyRegularBody200;
      color: var(--color-text-subtlest);
    }
  }

  &__scroll {
    flex: 1;
    overflow: auto;
    @include sectionAlignment;
  }

  &__container {
    display: flex;
    flex-direction: row;
    max-width: 768px;
    width: 100%;
    justify-content: stretch;
    flex-grow: 1;
  }

  &__body {
    flex-grow: 1;
    display: flex;
    flex-direction: column;

    position: relative;
    padding: 32px;
  }

  &__footer {
    position: relative;
    @include row;
    justify-content: flex-end;
    align-items: center;
    padding: 16px;

    &:after {
      content: '';
      position: absolute;
      top: -10px;
      left: 0;
      right: 0;
      height: 10px;
      background: linear-gradient(to top, rgba(145, 169, 192, 0.1), transparent);
      z-index: 1;
    }

    > * {
      min-width: 150px;
    }
  }

  &__cancel {
    margin-right: 12px;
  }
}
</style>
